import { z } from 'zod';
import {
  GitHubAppKindSchema,
  GitIntegrationPlatformSchema,
  GitIntegrationSchema,
  PrSyncModeSchema,
  RepositorySchema,
  CredentialsSchema,
  ApplicationEntryPointSchema,
  SubEntryPointWithRelationsSchema,
  CodeGraphWithRelationsSchema,
  CodeGraphNodeCategorySchema,
  AiFlowNodeCategorySchema,
  PrEndpointImpactSchema,
  ProcessedPullRequestStatusSchema,
} from '@terra/shared-schemas-git';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import { normalizeGitIntegrationBaseUrl } from './normalize-git-integration-base-url';

export { PrSyncModeSchema };

export const DEFAULT_POLLING_INTERVAL_HOURS = 2;

const OptionalGitIntegrationBaseUrlSchema = z
  .string()
  .optional()
  .transform((value) => normalizeGitIntegrationBaseUrl(value));

const NullableOptionalGitIntegrationBaseUrlSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) => normalizeGitIntegrationBaseUrl(value));

export const POLLING_INTERVAL_HOURS_MIN = 1;

export const POLLING_INTERVAL_HOURS_MAX = 168;

export const ListedRepositorySchema = RepositorySchema;

export type ListedRepository = z.infer<typeof ListedRepositorySchema>;

export const ExtendedCredentialsSchema = CredentialsSchema.omit({
  personalTokenEnc: true,
  gitHubAppId: true,
  gitHubAppPrivateKeyEnc: true,
}).extend({
  personalToken: z.string().nullable().optional(),
  gitHubAppId: z.string().trim().min(1).nullable().optional(),
  gitHubAppPrivateKey: z.string().trim().min(1).nullable().optional(),
  gitlabAuthCode: z.string().nullable().optional(),
  azureDevOpsAuthCode: z.string().nullable().optional(),
});

const PublicCredentialsSchema = CredentialsSchema.omit({
  gitHubAppPrivateKeyEnc: true,
});

export const ApplicationsIdSchema = z.object({
  applicationId: z.string(),
});

export const ApplicationGitIntegrationIdSchema = z.object({
  applicationId: z.string(),
  integrationId: z.string(),
});

export const GitInstallationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  repositories: z.array(RepositorySchema),
});

export const GitRepositoriesReturnSchema = z.object({
  repositories: ListedRepositorySchema.array(),
});

export const GitRepositoriesPagedReturnSchema = z.object({
  repositories: ListedRepositorySchema.array(),
  nextCursor: z.string().nullable().optional(),
});

export const GitRepositoriesRequestSchema = z.object({
  platform: GitIntegrationPlatformSchema,
  credentials: ExtendedCredentialsSchema,
  baseUrl: OptionalGitIntegrationBaseUrlSchema,
  apiVersion: z.string().optional(),
  gitHubAppKind: GitHubAppKindSchema.nullable().optional(),
  twingateNetworkId: z.string().nullish(),
  internalAccessNetworkId: z.string().nullish(),
});

export const ApplicationRepositoryByNameSchema = ApplicationsIdSchema.extend({
  repositoryName: z.string().trim().min(1),
});

export const GitRepositoryByNameReturnSchema = z.object({
  repositories: ListedRepositorySchema.array(),
});

export const GitBranchesRequestSchema = z.object({
  platform: GitIntegrationPlatformSchema,
  credentials: ExtendedCredentialsSchema,
  filter: z.string().optional(),
  repository: RepositorySchema.pick({
    id: true,
    name: true,
    owner: true,
  }),
  baseUrl: OptionalGitIntegrationBaseUrlSchema,
  apiVersion: z.string().optional(),
  gitHubAppKind: GitHubAppKindSchema.nullable().optional(),
  twingateNetworkId: z.string().nullish(),
  internalAccessNetworkId: z.string().nullish(),
});

export const GitBranchesReturnSchema = z.object({
  success: z.boolean(),
  branches: z.array(z.string()),
});

export const GitIntegrationItemSchema = z.object({
  id: z.string(),
  gitIntegrationId: z.string().nullable(),
  tenantId: z.string().nullable(),
  applicationId: z.string(),
  isWebhookEnabled: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  repositories: RepositorySchema.extend({
    lastPolledAt: z.coerce.date().nullable().optional(),
    lastProcessedMergedAt: z.coerce.date().nullable().optional(),
    lastProcessedPullNumber: z.int().nullable().optional(),
  }).array(),
  platform: GitIntegrationPlatformSchema,
  baseUrl: z.string().nullable(),
  apiVersion: z.string().nullable(),
  gitHubAppKind: GitHubAppKindSchema.nullable().optional(),
  type: z.enum(['APP', 'TOKEN']),
  isManualWebhook: z.boolean().optional(),
  prSyncMode: PrSyncModeSchema.optional(),
  pollingIntervalHours: z.int().optional(),
});

export type GitIntegrationItem = z.infer<typeof GitIntegrationItemSchema>;
export type GitIntegrationRepository =
  GitIntegrationItem['repositories'][number];

export const GitIntegrationsReturnSchema = z.object({
  gitIntegrations: GitIntegrationItemSchema.array(),
});

export const GitIntegrationNameSchema = z.string().trim().min(1).max(100);

const PublicGitIntegrationSchema = GitIntegrationSchema.omit({
  tailscaleNetworkId: true,
}).extend({
  internalAccessNetworkId: z.string().nullable(),
});

export const toPublicGitIntegration = <
  T extends { tailscaleNetworkId?: string | null },
>(
  integration: T,
): Omit<T, 'tailscaleNetworkId'> & {
  internalAccessNetworkId: string | null;
} => {
  const { tailscaleNetworkId, ...rest } = integration;

  return {
    ...rest,
    internalAccessNetworkId: tailscaleNetworkId ?? null,
  };
};

export const TenantGitIntegrationByIdReturnSchema = z.object({
  integration: PublicGitIntegrationSchema.extend({
    credentials: PublicCredentialsSchema,
    type: z.enum(['APP', 'TOKEN']),
    name: GitIntegrationNameSchema.nullable().optional(),
  }),
});

export const TenantGitIntegrationBodyBaseSchema = z.object({
  platform: GitIntegrationPlatformSchema,
  credentials: ExtendedCredentialsSchema,
  name: GitIntegrationNameSchema.optional(),
  baseUrl: NullableOptionalGitIntegrationBaseUrlSchema,
  apiVersion: z.string().nullable().optional(),
  gitHubAppKind: GitHubAppKindSchema.nullable().optional(),
  webhookSecret: z.string().nullable().optional(),
  twingateNetworkId: z.string().nullable().optional(),
  internalAccessNetworkId: z.string().nullable().optional(),
  prSyncMode: PrSyncModeSchema.optional(),
});

export const TenantGitIntegrationBodySchema =
  TenantGitIntegrationBodyBaseSchema.superRefine((data, context) => {
    const isGitHub =
      data.platform === 'GITHUB' || data.platform === 'GITHUB_ENTERPRISE';
    const isGitHubEnterprise = data.platform === 'GITHUB_ENTERPRISE';
    const hasInstallation = Boolean(data.credentials.gitHubInstallationId);

    if (isGitHub && hasInstallation && !data.gitHubAppKind) {
      context.addIssue({
        code: 'custom',
        path: ['gitHubAppKind'],
        message:
          'gitHubAppKind is required when using a GitHub App installation',
      });
    }

    if (isGitHubEnterprise && hasInstallation) {
      if (!data.baseUrl?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['baseUrl'],
          message: 'baseUrl is required for GitHub Enterprise Server',
        });
      }

      if (!data.credentials.gitHubAppId?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['credentials', 'gitHubAppId'],
          message: 'gitHubAppId is required for a GitHub Enterprise App',
        });
      }

      if (!data.credentials.gitHubAppPrivateKey?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['credentials', 'gitHubAppPrivateKey'],
          message:
            'gitHubAppPrivateKey is required for a GitHub Enterprise App',
        });
      }

      if (!data.webhookSecret?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['webhookSecret'],
          message: 'webhookSecret is required for a GitHub Enterprise App',
        });
      }
    }

    if (data.prSyncMode === 'MANUAL_WEBHOOK' && !data.webhookSecret?.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['webhookSecret'],
        message: 'webhookSecret is required when prSyncMode is MANUAL_WEBHOOK',
      });
    }
  });

export const TenantGitIntegrationReturnSchema = z.object({
  id: z.string(),
  success: z.boolean(),
});

export const TenantGitIntegrationsListReturnSchema = z.object({
  integrations: PublicGitIntegrationSchema.extend({
    credentials: PublicCredentialsSchema,
    type: z.enum(['APP', 'TOKEN']),
    applicationIds: z.array(z.string()),
    name: GitIntegrationNameSchema.nullable().optional(),
  }).array(),
});

export const TenantGitIntegrationIdSchema = z.object({
  integrationId: z.string(),
});

export const UpdateTenantGitIntegrationBodyBaseSchema = z.object({
  name: GitIntegrationNameSchema.optional(),
  twingateNetworkId: z.string().nullable().optional(),
  internalAccessNetworkId: z.string().nullable().optional(),
  prSyncMode: PrSyncModeSchema.optional(),
  webhookSecret: z.string().nullable().optional(),
  personalToken: z.string().trim().min(1).optional(),
});

export const UpdateTenantGitIntegrationBodySchema =
  UpdateTenantGitIntegrationBodyBaseSchema;

export const TenantGitIntegrationsQuerySchema = z.object({
  platforms: z.array(GitIntegrationPlatformSchema).optional(),
});

export const ApplicationGitIntegrationBodySchema = z.object({
  gitIntegrationId: z.string(),
  repositories: RepositorySchema.array(),
  pollingIntervalHours: z
    .int()
    .min(POLLING_INTERVAL_HOURS_MIN)
    .max(POLLING_INTERVAL_HOURS_MAX)
    .optional(),
});

export const ApplicationGitIntegrationReturnSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  gitIntegrationId: z.string(),
  success: z.boolean(),
});

export const RepositoryMappingSchema = z.object({
  zipRepositoryName: z.string().min(1),
  targetRepository: RepositorySchema.pick({
    id: true,
    name: true,
    owner: true,
    targetBranch: true,
  }),
});

export const TransformZipIntegrationBodySchema = z.object({
  zipAppIntegrationId: z.string(),
  tenantIntegrationId: z.string(),
  repositoryMappings: z.array(RepositoryMappingSchema).min(1),
});

export const TransformZipIntegrationReturnSchema = z.object({
  id: z.string(),
});

export const GitIntegrationBodySchema = z.object({
  platform: GitIntegrationPlatformSchema,
  credentials: ExtendedCredentialsSchema,
  repositories: RepositorySchema.array(),
  isWebhookEnabled: z.boolean().optional(),
  baseUrl: NullableOptionalGitIntegrationBaseUrlSchema,
  apiVersion: z.string().nullable().optional(),
  webhookSecret: z.string().nullable().optional(),
  gitHubAppKind: GitHubAppKindSchema.nullable().optional(),
  prSyncMode: PrSyncModeSchema.optional(),
  pollingIntervalHours: z
    .int()
    .min(POLLING_INTERVAL_HOURS_MIN)
    .max(POLLING_INTERVAL_HOURS_MAX)
    .optional(),
});

export const GitIntegrationUpsertBodySchema = z
  .object({
    repositories: RepositorySchema.array().optional(),
    isWebhookEnabled: z.boolean().optional(),
    pollingIntervalHours: z
      .int()
      .min(POLLING_INTERVAL_HOURS_MIN)
      .max(POLLING_INTERVAL_HOURS_MAX)
      .optional(),
  })
  .refine(
    (data) =>
      data.repositories !== undefined ||
      data.isWebhookEnabled !== undefined ||
      data.pollingIntervalHours !== undefined,
    {
      error:
        'At least one field (repositories, isWebhookEnabled, or pollingIntervalHours) must be provided',
    },
  );

export const ManualWebhookRepositorySchema = RepositorySchema.pick({
  id: true,
  name: true,
  owner: true,
});

export const ManualWebhookVerificationSchema = z.object({
  applicationId: z.string().optional(),
  gitIntegrationId: z.string().optional(),
  platform: GitIntegrationPlatformSchema,
  credentials: ExtendedCredentialsSchema,
  repositories: ManualWebhookRepositorySchema.array().optional(),
  baseUrl: NullableOptionalGitIntegrationBaseUrlSchema,
  apiVersion: z.string().nullable().optional(),
  twingateNetworkId: z.string().nullable().optional(),
  internalAccessNetworkId: z.string().nullable().optional(),
});

export const ManualWebhookVerificationResultSchema = z.object({
  success: z.boolean(),
  missingRepositories: z.array(z.string()),
  expectedUrl: z.string(),
  message: z.string().optional(),
});

export const ApplicationWebhookUrlReturnSchema = z.object({
  expectedUrl: z.string().nullable(),
  decodedWebhookSecret: z.string().nullable(),
});

export const GitIntegrationUpsertReturnSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  success: z.boolean(),
});

export const GitRepositoryReturnSchema = z.object({
  success: z.boolean(),
  repositories: ListedRepositorySchema.array(),
});

export const GitAggregatedMetricsSchema = z.object({
  totalNumberOfScannedPullRequests: z.number(),
  totalNumberOfEntryPointsFound: z.number(),
  totalNumberOfFilesIndexed: z.number(),
  totalNumberOfRelevantFilesFound: z.number(),
});

export const ApplicationMetricsReturnSchema = z.object({
  metrics: z
    .object({
      numberOfScannedPullRequests: z.number(),
      numberOfEntryPointsFound: z.number(),
      numberOfRelevantFilesFound: z.number(),
      numberOfFilesIndexed: z.number(),
    })
    .nullable(),
});

export const UpdateApplicationEntryPointSchema =
  ApplicationEntryPointSchema.omit({
    id: true,
    applicationId: true,
    createdAt: true,
    updatedAt: true,
  }).partial();

export type UpdateApplicationEntryPointDto = z.infer<
  typeof UpdateApplicationEntryPointSchema
>;

export const GroupedEntryPointsSchema = z.object({
  pullNumber: z.number(),
  prOwner: z.string(),
  ownerAvatar: z.string(),
  prTitle: z.string(),
  prUrl: z.string(),
  createdAt: z.date(),
  entryPoints: z.array(
    ApplicationEntryPointSchema.pick({
      fileName: true,
      description: true,
      codeSnippet: true,
      entryPoint: true,
      id: true,
      investigated: true,
      enhancementStartedAt: true,
      isReachable: true,
      allSubGraphsFailed: true,
    }).extend({
      subEntryPoints: SubEntryPointWithRelationsSchema.array(),
    }),
  ),
});

export const GitEntryPointsReturnSchema = z.object({
  entryPoints: GroupedEntryPointsSchema.array(),
});

export const ProcessedPullRequestListItemSchema =
  GroupedEntryPointsSchema.extend({
    status: ProcessedPullRequestStatusSchema,
    endpointsImpacts: PrEndpointImpactSchema.array(),
    isRemediation: z.boolean().default(false),
  });

export const GitProcessedPullRequestsReturnSchema = z.object({
  processedPullRequests: ProcessedPullRequestListItemSchema.array(),
  total: z.number(),
  skip: z.number(),
  limit: z.number(),
});

export const GitEntryPointsQuerySchema = PaginationQueryParamsSchema.extend({
  entryPointId: z.string().optional(),
  endpointId: z.string().optional(),
  searchTerm: z.string().optional(),
  entryPointTypes: z.array(z.string()).optional(),
  timeFrame: z.enum(['24h', '7d', '30d', '90d']).optional(),
  createdStartDate: z.coerce.date().optional(),
  createdEndDate: z.coerce.date().optional(),
  isReachable: z.coerce.boolean().optional(),
  hasSignals: z.coerce.boolean().optional(),
  investigated: z
    .union([
      z.boolean(),
      z.enum(['true', 'false']).transform((value) => value === 'true'),
    ])
    .optional(),
});

export const GitProcessedPullRequestsCountQuerySchema =
  GitEntryPointsQuerySchema.omit({
    skip: true,
    limit: true,
    orderBy: true,
  }).extend({
    isRemediation: z
      .union([
        z.boolean(),
        z.enum(['true', 'false']).transform((value) => value === 'true'),
      ])
      .optional(),
  });

export const GitProcessedPullRequestsQuerySchema =
  GitEntryPointsQuerySchema.extend({
    isRemediation: GitProcessedPullRequestsCountQuerySchema.shape.isRemediation,
  });

export const GitDateRangeQuerySchema = z.object({
  createdStartDate: z.coerce.date().optional(),
  createdEndDate: z.coerce.date().optional(),
});

export const GitCountReturnSchema = z.object({
  count: z.number(),
});

export const EndpointFileSchema = z.object({
  id: z.string(),
  filePath: z.string(),
  repositoryName: z.string(),
  startLine: z.number().optional(),
  endLine: z.number().optional(),
  nodeCategory: CodeGraphNodeCategorySchema.nullable(),
  nodeName: z.string(),
  isEntryPoint: z.boolean(),
});

export type EndpointFile = z.infer<typeof EndpointFileSchema>;

export const EndpointFilesReturnSchema = z.object({
  files: EndpointFileSchema.array(),
});

export const FileContentReturnSchema = z.object({
  content: z.string(),
  language: z.string(),
  filePath: z.string(),
  repositoryName: z.string(),
});

export const EndpointCodeGraphReturnSchema = z.object({
  codeGraph: CodeGraphWithRelationsSchema,
});

export const EndpointCodeGraphErrorSchema = z.object({
  message: z.string(),
});

export const GitOrganizationsRequestSchema = z.object({
  platform: z.literal('AZURE_DEVOPS'),
  authCode: z.string(),
});

export const GitOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
});

export const GitOrganizationsReturnSchema = z.object({
  organizations: GitOrganizationSchema.array(),
});

export const AiFlowNodeSchema = z.object({
  id: z.string(),
  type: AiFlowNodeCategorySchema,
  name: z.string(),
  filePath: z.string(),
  startLine: z.number().nullable().optional(),
  endLine: z.number().nullable().optional(),
  repositoryName: z.string(),
  framework: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  spawnsAgentAt: z.string().nullable().optional(),
});

export const AiFlowEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  type: z.string(),
  label: z.string().nullable().optional(),
});

export const TechnologySchema = z.object({
  name: z.string(),
  version: z.string().nullable().optional(),
  category: z.string(),
  purpose: z.string(),
  repositoryName: z.string(),
  relatedNodeIds: z.array(z.string()),
  confidence: z.string(),
});

export const AiFlowGraphReturnSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable().optional(),
  applicationId: z.string(),
  endpointId: z.string().nullable().optional(),
  parentGraphId: z.string().nullable().optional(),
  parentToolNodeId: z.string().nullable().optional(),
  nodes: z.array(AiFlowNodeSchema),
  edges: z.array(AiFlowEdgeSchema),
  entryPointNodeIds: z.array(z.string()),
  description: z.string(),
  method: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  framework: z.string().nullable().optional(),
  technologies: z.array(TechnologySchema).optional(),
  childGraphIds: z.array(z.string()),
  isOutdated: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type AiFlowGraphReturnDto = z.infer<typeof AiFlowGraphReturnSchema>;

export const AiFlowNodeCodeGraphSchema = z.object({
  aiFlowNodeId: z.string(),
  aiFlowNodeType: AiFlowNodeCategorySchema,
  codeGraphId: z.string(),
});

export const AiFlowNodeCodeGraphsReturnSchema = z.object({
  codeGraphs: z.array(AiFlowNodeCodeGraphSchema),
});

export const JenkinsDeploymentWebhookBodySchema = z
  .object({
    schemaVersion: z.int().describe('Payload schema version. Currently 1.'),
    repos: z
      .array(z.string())
      .min(1)
      .describe(
        'Git repository URLs (https or scp/SSH form) deployed in this build.',
      ),
    branch: z.string().describe('Deployed branch name.'),
    environment: z
      .string()
      .describe('Target environment. Only "production" triggers enhancement.'),
    status: z
      .string()
      .describe('Deployment result. Only "SUCCESS" is processed.'),
    jobName: z.string().describe('Jenkins job name.'),
    buildNumber: z.int().describe('Jenkins build number.'),
    buildUrl: z.string().describe('URL of the Jenkins build.'),
    commitSha: z
      .string()
      .min(1)
      .optional()
      .describe(
        'Git commit SHA deployed in this build. When provided, Terra scopes testing to entry points changed in this commit.',
      ),
    versionTag: z
      .string()
      .min(1)
      .optional()
      .describe(
        'Release or version tag of the deployment. When provided, Terra scopes testing to entry points changed in this release.',
      ),
  })
  .describe('Jenkins deployment-success webhook payload.');

export type JenkinsDeploymentWebhookBody = z.infer<
  typeof JenkinsDeploymentWebhookBodySchema
>;
